import { useState, type ReactNode } from 'react'
import { Check, HelpCircle, Link2, Undo2, X } from 'lucide-react'
import { Field, NumberInput, TextInput } from '../admin/form.js'
import { usePermissions } from '../admin/primitives.js'
import { useConfirm } from '../shell/confirm.js'
import { Link } from '../router.js'
import { ARattacher } from '../soirees/Rattachements.js'
import { NuitsDesEvenements, titreNuitsDesEvenements } from '../soirees/NuitsEvenementsAssociations.js'
import { useGestes, useLecture } from './associations-api.js'
import { Bloc, ChoixAssociation, Chargement, Erreur, PastilleKind } from './AssociationsCommun.js'
import { useMenuSoiree } from '../soirees/menu-soiree.js'
import {
  aideEntreesNuit, cheminFiche, compte, detailADecider, libelleSource, nombre, nuitLongue, texteVerdict,
  type Ecartees, type EvenementADecider, type LigneAssociation, type NuitSansNom, type ReponseADecider, type SuggestionADecider,
} from './associations-model.js'

/**
 * « À DÉCIDER » — ce que Gestion ne tranche pas seul (20/10/2026).
 *
 * Dans l'ordre de l'URGENCE (revue du 20/10) :
 *   · « À rattacher » d'abord — DÉMÉNAGÉE de Réglages › Billetteries : à
 *     quelle soirée du club appartient un événement d'association. C'est la
 *     seule décision qui presse (avant la nuit), et celle où mènera l'alerte ;
 *     elle garde ses propres droits (« Consulter / Gérer les soirées ») ;
 *   · les associations SUGGÉRÉES (15 au premier lancement) : confirmer, en
 *     la renommant au besoin, ou rejeter. Jamais confirmées d'office ;
 *   · les soirées qui ressemblent à celles d'une association sans que Gestion
 *     sache laquelle : lier, ou « pas une association » ;
 *   · les nuits sans nom : une soirée Shotgun vendue en bloc un soir de
 *     semaine, ou une nuit que seule la Comptabilité connaît (« N × 6 € ») :
 *     lier, marquer la nuit à la main, ou l'écarter ;
 *   · « Écartées » : ce qui a été rejeté ou dit « pas une association »,
 *     pour y revenir APRÈS le message « ANNULER » de 9 secondes — sans elle,
 *     un clic de travers faisait disparaître une soirée de tout écran.
 *
 * « La nuit (et la part) de chaque événement d'association », l'autre liste
 * déménagée, n'est pas une décision en attente : elle vit sous la table des
 * associations, repliée (`NuitsDesEvenementsBloc`).
 *
 * Chaque geste est immédiat, s'annule (« ANNULER ») et n'est jamais retouché
 * par la passe automatique.
 */

export function ADecider({ associations }: { associations: readonly LigneAssociation[] }) {
  const { etat, relire } = useLecture<ReponseADecider>('/api/v1/associations/a-decider')
  const permissions = usePermissions()
  const litSoirees = permissions.permissions.includes('soiree.read')

  return (
    <Bloc id="a-decider" titre="À décider" className="asso-adecider">
      {litSoirees ? (
        <div className="asso-adecider__partie">
          <h3 className="asso-sous-titre">À rattacher à la soirée du club</h3>
          <p className="lc-hint">
            Un événement d’association (Pretix) compte avec la soirée du club de la même nuit. Ce que Gestion n’a pas pu
            rattacher seul attend ici, à décider avant la nuit : la soirée proposée est présélectionnée, avec ses raisons.
          </p>
          {/* La nuit d'un événement se corrige PLUS BAS sur cette page (« Corriger la nuit d’un événement d’association »). */}
          <ARattacher complet ouCorrigerLaNuit="plus bas sur cette page, « Corriger la nuit d’un événement d’association »" />
        </div>
      ) : null}
      {etat.phase === 'chargement' ? <Chargement libelle="Lecture de ce qui reste à décider" />
        : etat.phase === 'erreur' ? <Erreur message={etat.message} onReessayer={relire} />
          : <Contenu d={etat.data} associations={associations} />}
      <EcarteesPartie associations={associations} />
    </Bloc>
  )
}

/**
 * « La nuit (et la part) de chaque événement d'association » — une liste de
 * CORRECTIONS, pas de décisions en attente : repliée, sous la table, pour ne
 * pas repousser les associations à chaque visite (11 événements aujourd'hui,
 * chacun avec son formulaire de part, et la liste grandit).
 */
export function NuitsDesEvenementsBloc() {
  const permissions = usePermissions()
  if (!permissions.permissions.includes('soiree.read')) return null
  return (
    <Bloc id="nuits-evenements" titre={titreNuitsDesEvenements(permissions.permissions.includes('finance.reference.read'))}>
      <details className="asso-details asso-details--bloc">
        <summary>Corriger la nuit d’un événement d’association{permissions.permissions.includes('finance.reference.read') ? ' ou la part par billet' : ''}</summary>
        <NuitsDesEvenements />
      </details>
    </Bloc>
  )
}

function Contenu({ d, associations }: { d: ReponseADecider; associations: readonly LigneAssociation[] }) {
  const gerer = d.droits.gerer
  const cotesAssociations = d.suggestions.length + d.evenements.length + d.nuits_sans_nom.length
  return (
    <div className="asso-adecider__parties">
      {d.total > 0 ? <p className="asso-adecider__resume">
        {detailADecider({ suggestions: d.suggestions.length, evenements: d.evenements.length, nuits_sans_nom: d.nuits_sans_nom.length, a_rattacher: d.a_rattacher })}
        {gerer || cotesAssociations === 0 ? null : <span className="lc-hint"> — vous pouvez consulter ; décider demande « Gérer les associations ».</span>}
      </p> : null}
      {cotesAssociations === 0 ? (
        <p className="lc-hint asso-rien">Rien à décider côté associations : chaque soirée reconnue est liée, et aucune suggestion n’attend.</p>
      ) : null}

      {d.suggestions.length > 0 ? (
        <div className="asso-adecider__partie">
          <h3 className="asso-sous-titre">Associations suggérées</h3>
          <p className="lc-hint">
            Gestion les a trouvées dans l’historique. Rien ne leur est lié tant que vous ne les confirmez pas ; l’école et le
            sens des sigles sont des hypothèses. Confirmez (en renommant au besoin) ou rejetez chacune.
          </p>
          <ul className="asso-cartes">
            {d.suggestions.map((s) => <CarteSuggestion key={s.association.id} s={s} gerer={gerer} />)}
          </ul>
        </div>
      ) : null}

      {d.evenements.length > 0 ? (
        <div className="asso-adecider__partie">
          <h3 className="asso-sous-titre">Soirées à identifier</h3>
          <p className="lc-hint">Elles ressemblent à des soirées d’association, mais Gestion ne lie pas seul. Choisissez l’association, ou dites que ce n’en est pas une.</p>
          <ul className="asso-cartes">
            {d.evenements.map((e) => <CarteEvenement key={e.event_id} e={e} gerer={gerer} associations={associations} motifMin={d.motif_min} />)}
          </ul>
        </div>
      ) : null}

      {d.nuits_sans_nom.length > 0 ? (
        <div className="asso-adecider__partie">
          <h3 className="asso-sous-titre">Nuits sans nom</h3>
          <p className="lc-hint">
            Des billets d’un seul tarif saisis en bloc un soir de semaine, ou des entrées en Comptabilité sans aucune soirée
            dans la billetterie : le motif des soirées d’associations vendues sur place. Ce sont des hypothèses, à vérifier.
            Une nuit que Gestion ne voit pas du tout (ni billetterie ni Comptabilité) se marque depuis la fiche de son
            association, « Marquer une nuit à la main ».
          </p>
          <ul className="asso-cartes">
            {d.nuits_sans_nom.map((n) => <CarteNuitSansNom key={n.cle} n={n} gerer={gerer} associations={associations} />)}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

/* ═══════════════════════════════════════ une suggestion ═══ */

function CarteSuggestion({ s, gerer }: { s: SuggestionADecider; gerer: boolean }) {
  const menuSoiree = useMenuSoiree()
  const { envoyer } = useGestes()
  const a = s.association
  const [mode, setMode] = useState<'confirmer' | 'rejeter' | null>(null)
  const [nom, setNom] = useState(a.name)
  const [motif, setMotif] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)
  const vendus = s.reconnues.reduce((t, x) => t + x.vendus, 0)
  const agir = async () => {
    setEnvoi(true); setErreur(null)
    try {
      const e = mode === 'confirmer'
        ? await envoyer('POST', `/api/v1/associations/${a.id}/confirmer`, { name: nom.trim() && nom.trim() !== a.name ? nom.trim() : null })
        : await envoyer('POST', `/api/v1/associations/${a.id}/rejeter`, { motif: motif.trim() || null })
      if (e) setErreur(e); else setMode(null)
    } finally { setEnvoi(false) }
  }
  return (
    <li className="asso-carte">
      <div className="asso-carte__tete">
        <strong className="asso-carte__nom">{a.name}</strong>
        <PastilleKind kind={a.kind} />
        {a.member_of_name ? <span className="lc-hint">membre de {a.member_of_name}</span> : null}
      </div>
      <p className="asso-carte__faits">
        {s.reconnues.length > 0
          ? <>Une fois confirmée, {compte(s.reconnues.length, 'soirée compte', 'soirées comptent')} dans ses chiffres ({nombre(vendus)} billets vendus).</>
          : <>Aucune soirée de l’historique ne la désigne encore par son nom.</>}
        {a.school ? <> École supposée : {a.school}.</> : null}
      </p>
      {a.suggestion_reason ? <p className="lc-hint asso-carte__pourquoi">Pourquoi Gestion la propose : {a.suggestion_reason}</p> : null}
      {s.alias.length > 0 ? (
        <p className="asso-carte__alias"><span className="lc-hint">Reconnue par :</span> {s.alias.map((x) => <span key={x.id} className="asso-puce">{x.label}</span>)}</p>
      ) : null}
      {s.reconnues.length > 0 ? (
        <details className="asso-details">
          <summary>Ses soirées</summary>
          <ul className="asso-liste-simple">
            {s.reconnues.map((r) => (
              <li key={r.event_id}><Link href={`/soirees/${r.event_id}`} {...menuSoiree.soiree({ id: r.event_id, name: r.name })}>{r.name}</Link> · {nuitLongue(r.nuit)} · {libelleSource(r.source)} · {nombre(r.vendus)} billets{r.confidence === 'PROBABLE' ? ' · au nom seul (probable)' : ''}</li>
            ))}
          </ul>
        </details>
      ) : null}
      {gerer && mode === null ? (
        <div className="asso-carte__gestes">
          <button type="button" className="lc-btn lc-btn--primary lc-btn--sm" onClick={() => setMode('confirmer')}><Check size={14} aria-hidden="true" /> Confirmer…</button>
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => setMode('rejeter')}><X size={14} aria-hidden="true" /> Rejeter…</button>
        </div>
      ) : null}
      {mode ? (
        <div className="asso-carte__form">
          {mode === 'confirmer' ? (
            <Field label="Son nom" helper="Renommez-la si le nom proposé ne convient pas ; ses alias restent.">
              <TextInput value={nom} onChange={setNom} maxLength={80} ariaLabel={`Nom de ${a.name}`} />
            </Field>
          ) : (
            <Field label="Pourquoi (facultatif)" helper="Elle ne sera plus proposée ; ses soirées restent sans association. « Écartées », plus bas, permet d’y revenir.">
              <TextInput value={motif} onChange={setMotif} maxLength={300} ariaLabel="Motif du rejet" />
            </Field>
          )}
          {erreur ? <p className="lc-helper lc-helper--error" role="alert">{erreur}</p> : null}
          <div className="asso-carte__gestes">
            <button type="button" className="lc-btn lc-btn--primary lc-btn--sm" disabled={envoi || (mode === 'confirmer' && !nom.trim())} onClick={() => { void agir() }}>
              {envoi ? 'Envoi…' : mode === 'confirmer' ? 'Confirmer l’association' : 'Rejeter la suggestion'}
            </button>
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => { setMode(null); setErreur(null) }}>Annuler</button>
          </div>
        </div>
      ) : null}
    </li>
  )
}

/* ═══════════════════════════════════════ une soirée à identifier ═══ */

function CarteEvenement({ e, gerer, associations, motifMin }: { e: EvenementADecider; gerer: boolean; associations: readonly LigneAssociation[]; motifMin: number }) {
  const menuSoiree = useMenuSoiree()
  const { envoyer } = useGestes()
  const proposee = e.suggestion?.id ?? e.candidats[0]?.id ?? ''
  // Présélectionnée seulement si elle peut recevoir la soirée (une association archivée ne le peut pas).
  const [choix, setChoix] = useState(associations.some((a) => a.id === proposee && a.status === 'ACTIVE') ? proposee : '')
  const [motif, setMotif] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)
  // Choisir AUTRE CHOSE que ce que Gestion reconnaît demande un motif (le serveur le redit au besoin).
  // Entre plusieurs associations possibles (AMBIGU), en choisir une autre de la liste n'a rien d'inhabituel.
  const autre = proposee !== '' && choix !== proposee && !(e.verdict === 'AMBIGU' && e.candidats.some((c) => c.id === choix))
  const lier = async () => {
    setEnvoi(true); setErreur(null)
    try {
      const r = await envoyer('POST', '/api/v1/associations/liens/lier', { evenements: [e.event_id], association_id: choix, motif: motif.trim() || null })
      if (r) setErreur(r)
    } finally { setEnvoi(false) }
  }
  const pasAsso = async () => {
    setEnvoi(true); setErreur(null)
    try {
      const r = await envoyer('POST', '/api/v1/associations/liens/pas-association', { evenements: [e.event_id], motif: motif.trim() || null })
      if (r) setErreur(r)
    } finally { setEnvoi(false) }
  }
  return (
    <li className="asso-carte">
      <div className="asso-carte__tete">
        <Link href={`/soirees/${e.event_id}`} className="asso-carte__nom" {...menuSoiree.soiree({ id: e.event_id, name: e.name })}>{e.name}</Link>
        <span className="lc-pill lc-pill--warning"><HelpCircle size={12} aria-hidden="true" /> {texteVerdict(e.verdict)}</span>
      </div>
      <p className="asso-carte__faits">{nuitLongue(e.nuit)} · {libelleSource(e.source)} · {nombre(e.vendus)} billets vendus</p>
      <p className="lc-hint asso-carte__pourquoi">{e.raison}</p>
      {gerer ? (
        <div className="asso-carte__form asso-carte__form--ligne">
          <ChoixAssociation label="Association" associations={associations} value={choix} onChange={setChoix} proposee={proposee || null} />
          {autre || e.verdict === 'AMBIGU' ? (
            <Field label={autre ? `Pourquoi pas « ${associations.find((a) => a.id === proposee)?.name ?? e.suggestion?.name ?? 'la proposée'} »` : 'Pourquoi (facultatif)'}
              helper={autre ? `Quelques mots (${motifMin} caractères au moins) : le choix est tracé.` : undefined}>
              <TextInput value={motif} onChange={setMotif} maxLength={300} ariaLabel="Motif" />
            </Field>
          ) : null}
          {erreur ? <p className="lc-helper lc-helper--error" role="alert">{erreur}</p> : null}
          <div className="asso-carte__gestes">
            <button type="button" className="lc-btn lc-btn--primary lc-btn--sm" disabled={envoi || !choix || (autre && motif.trim().length < motifMin)} onClick={() => { void lier() }}>
              <Link2 size={14} aria-hidden="true" /> Lier à {associations.find((a) => a.id === choix)?.name ?? '…'}
            </button>
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" disabled={envoi} onClick={() => { void pasAsso() }}>Pas une association</button>
          </div>
        </div>
      ) : null}
    </li>
  )
}

/* ═══════════════════════════════════════ une nuit sans nom ═══ */

function CarteNuitSansNom({ n, gerer, associations }: { n: NuitSansNom; gerer: boolean; associations: readonly LigneAssociation[] }) {
  const menuSoiree = useMenuSoiree()
  const { envoyer } = useGestes()
  const avecSoiree = n.origine === 'BILLETS' && n.event_id !== null
  const [choix, setChoix] = useState('')
  const [geste, setGeste] = useState<'lier' | 'marquer'>(avecSoiree ? 'lier' : 'marquer')
  const [entrees, setEntrees] = useState('')
  const [note, setNote] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)
  const nomChoisi = associations.find((a) => a.id === choix)?.name
  const agir = async (quoi: 'lier' | 'marquer' | 'pas') => {
    setEnvoi(true); setErreur(null)
    try {
      const e = quoi === 'pas'
        ? avecSoiree
          ? await envoyer('POST', '/api/v1/associations/liens/pas-association', { evenements: [n.event_id], motif: note.trim() || null })
          : await envoyer('POST', '/api/v1/associations/nuits/ecarter', { nuit: n.nuit, motif: note.trim() || null })
        : quoi === 'lier' && avecSoiree
          ? await envoyer('POST', '/api/v1/associations/liens/lier', { evenements: [n.event_id], association_id: choix, motif: note.trim() || null })
          : await envoyer('POST', `/api/v1/associations/${choix}/nuits`, {
            nuit: n.nuit, entrees: entrees.trim() === '' ? null : Math.max(0, Math.round(Number(entrees))), note: note.trim() || null,
          })
      if (e) setErreur(e)
    } finally { setEnvoi(false) }
  }
  const entreesInvalides = entrees.trim() !== '' && !(Number.isFinite(Number(entrees)) && Number(entrees) >= 0)
  const marquer = geste === 'marquer' || !avecSoiree
  return (
    <li className="asso-carte">
      <div className="asso-carte__tete">
        {avecSoiree ? <Link href={`/soirees/${n.event_id}`} className="asso-carte__nom" {...(n.event_id && n.name ? menuSoiree.soiree({ id: n.event_id, name: n.name }) : {})}>{n.name}</Link>
          : <strong className="asso-carte__nom">Nuit du {nuitLongue(n.nuit)}</strong>}
        {avecSoiree ? <span className="lc-pill lc-pill--idle">{nuitLongue(n.nuit)}</span> : <span className="lc-pill lc-pill--idle">Comptabilité seule</span>}
      </div>
      <p className="asso-carte__faits">
        {avecSoiree ? <>{nombre(n.vendus)} billets · {n.produits.join(', ')}</> : <>{nombre(n.entrees_compta)} entrées en Comptabilité · aucune soirée vendue</>}
      </p>
      <p className="lc-hint asso-carte__pourquoi">{n.raison}</p>
      {gerer ? (
        <div className="asso-carte__form asso-carte__form--ligne">
          <ChoixAssociation label="Association" associations={associations} value={choix} onChange={setChoix} />
          {avecSoiree ? (
            <div className="asso-choix-geste" role="radiogroup" aria-label="Ce que l’association reçoit">
              <label><input type="radio" name={`g-${n.cle}`} checked={geste === 'lier'} onChange={() => setGeste('lier')} /> Ses {nombre(n.vendus)} billets comptent pour elle</label>
              <label><input type="radio" name={`g-${n.cle}`} checked={geste === 'marquer'} onChange={() => setGeste('marquer')} /> Seulement la nuit (les billets restent au club)</label>
            </div>
          ) : null}
          {marquer ? (
            <Field label="Entrées (facultatif)" helper={aideEntreesNuit(n)}>
              <NumberInput value={entrees} onChange={setEntrees} min={0} max={100000} ariaLabel="Entrées de la nuit" />
            </Field>
          ) : null}
          <Field label="Note (facultatif)">
            <TextInput value={note} onChange={setNote} maxLength={300} ariaLabel="Note" placeholder="INTERFAC, 6 € = 8 € moins la part de 2 €" />
          </Field>
          {erreur ? <p className="lc-helper lc-helper--error" role="alert">{erreur}</p> : null}
          <div className="asso-carte__gestes">
            <button type="button" className="lc-btn lc-btn--primary lc-btn--sm" disabled={envoi || !choix || (marquer && entreesInvalides)} onClick={() => { void agir(avecSoiree ? geste : 'marquer') }}>
              {nomChoisi ? `Attribuer à ${nomChoisi}` : 'Attribuer'}
            </button>
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" disabled={envoi} onClick={() => { void agir('pas') }}>
              {avecSoiree ? 'Pas une association' : 'Pas une nuit d’association'}
            </button>
          </div>
        </div>
      ) : null}
    </li>
  )
}

/* ═══════════════════════════════════════ « Écartées » ═══ */

/**
 * Ce qui a été écarté — rejeté, « pas une association », nuit écartée —
 * avec « Annuler » : la décision revient où elle était (« À décider », ou la
 * suggestion). Le geste « pas une association » d'une soirée jamais liée
 * n'appartient à aucune fiche : sans cette liste, il n'était annulable que
 * pendant les 9 secondes du message.
 */
function EcarteesPartie({ associations }: { associations: readonly LigneAssociation[] }) {
  const menuSoiree = useMenuSoiree()
  const { etat } = useLecture<Ecartees>('/api/v1/associations/ecartees')
  if (etat.phase !== 'ok') return null
  const e = etat.data
  const n = e.suggestions.length + e.evenements.length + e.nuits.length
  if (n === 0) return null
  return (
    <details className="asso-details asso-adecider__partie" id="ecartees">
      <summary>Écartées ({nombre(n)}) : suggestions rejetées, soirées et nuits « pas une association »</summary>
      <div className="asso-ecartees">
        <p className="lc-hint">« Annuler » remet la décision où elle était ; une soirée peut aussi être liée directement à une association.</p>
        {e.suggestions.length > 0 ? (
          <>
            <h3 className="asso-sous-titre">Suggestions rejetées</h3>
            <ul className="asso-ecartees__liste">
              {e.suggestions.map((s) => (
                <LigneEcartee key={s.id} geste={s.geste_id} gerer={e.droits.gerer} libelleAnnuler="Annuler le rejet"
                  quoi={`Suggestion « ${s.name} » rejetée`}>
                  <Link href={cheminFiche(s.id)}>{s.name}</Link> <PastilleKind kind={s.kind} />
                  <span className="lc-hint"> · rejetée le {dateCourte(s.at)}{s.reason ? ` — « ${s.reason} »` : ''}</span>
                </LigneEcartee>
              ))}
            </ul>
          </>
        ) : null}
        {e.evenements.length > 0 ? (
          <>
            <h3 className="asso-sous-titre">Soirées « pas une association »</h3>
            <ul className="asso-ecartees__liste">
              {e.evenements.map((x) => (
                <LigneEcartee key={x.event_id} geste={x.geste_id} gerer={e.droits.gerer}
                  libelleAnnuler={x.geste_evenements > 1 ? `Annuler (${x.geste_evenements} soirées)` : 'Annuler'}
                  quoi={`« ${x.name} » : pas une soirée d’association`}
                  lier={{ evenement: x.event_id, associations }}>
                  <Link href={`/soirees/${x.event_id}`} {...menuSoiree.soiree({ id: x.event_id, name: x.name })}>{x.name}</Link>
                  <span className="lc-hint"> · {nuitLongue(x.nuit)} · {libelleSource(x.source)} · {nombre(x.vendus)} billets · le {dateCourte(x.at)}{x.actor_name ? ` par ${x.actor_name}` : ''}{x.reason ? ` — « ${x.reason} »` : ''}</span>
                </LigneEcartee>
              ))}
            </ul>
          </>
        ) : null}
        {e.nuits.length > 0 ? (
          <>
            <h3 className="asso-sous-titre">Nuits « pas une nuit d’association »</h3>
            <ul className="asso-ecartees__liste">
              {e.nuits.map((x) => (
                <LigneEcartee key={x.nuit} geste={x.geste_id} gerer={e.droits.gerer} libelleAnnuler="Annuler"
                  quoi={`Nuit du ${nuitLongue(x.nuit)} écartée`}>
                  <strong>Nuit du {nuitLongue(x.nuit)}</strong>
                  <span className="lc-hint"> · le {dateCourte(x.at)}{x.actor_name ? ` par ${x.actor_name}` : ''}{x.reason ? ` — « ${x.reason} »` : ''}</span>
                </LigneEcartee>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    </details>
  )
}

const dateCourte = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Paris' })

function LigneEcartee({ geste, gerer, libelleAnnuler, quoi, lier, children }: {
  geste: string | null; gerer: boolean; libelleAnnuler: string; quoi: string
  lier?: { evenement: string; associations: readonly LigneAssociation[] }
  children: ReactNode
}) {
  const { annuler, envoyer } = useGestes()
  const confirmer = useConfirm()
  const [ouvert, setOuvert] = useState(false)
  const [choix, setChoix] = useState('')
  const [motif, setMotif] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const lierA = async () => {
    setErreur(null)
    const r = await envoyer('POST', '/api/v1/associations/liens/lier', { evenements: [lier!.evenement], association_id: choix, motif: motif.trim() || null })
    if (r) setErreur(r); else setOuvert(false)
  }
  return (
    <li className="asso-ecartees__ligne">
      <span className="asso-ecartees__texte">{children}</span>
      {gerer ? (
        <span className="asso-carte__gestes">
          {geste ? (
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={async () => {
              const ok = await confirmer({ title: 'Annuler cette décision ?', message: `${quoi} : Gestion rétablit ce qu’il y avait avant, sauf ce qui a été retouché depuis.`, confirmLabel: 'Annuler la décision', cancelLabel: 'Garder' })
              if (ok) await annuler(geste)
            }}><Undo2 size={14} aria-hidden="true" /> {libelleAnnuler}</button>
          ) : null}
          {lier && !ouvert ? <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => setOuvert(true)}><Link2 size={14} aria-hidden="true" /> Lier à…</button> : null}
        </span>
      ) : null}
      {lier && ouvert ? (
        <div className="asso-carte__form asso-carte__form--ligne">
          <ChoixAssociation label="Lier à" associations={lier.associations} value={choix} onChange={setChoix} />
          <Field label="Pourquoi (facultatif)">
            <TextInput value={motif} onChange={setMotif} maxLength={300} ariaLabel="Motif" />
          </Field>
          {erreur ? <p className="lc-helper lc-helper--error" role="alert">{erreur}</p> : null}
          <div className="asso-carte__gestes">
            <button type="button" className="lc-btn lc-btn--primary lc-btn--sm" disabled={!choix} onClick={() => { void lierA() }}>Lier</button>
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => { setOuvert(false); setErreur(null) }}>Fermer</button>
          </div>
        </div>
      ) : null}
    </li>
  )
}
